import React from 'react';
import { CircularProgress, Divider } from '@mui/material';
import { DeleteOutlined } from '@mui/icons-material';
import CustomSelect from '../common/CustomSelect';
import SelectWithInput from '../common/SelectWithInput';
import CustomInput from '../TransactionForm/CustomInput';
import TransactionCommonModal from '../common/TransactionCommonModal';
import { fiatCurrencyList } from '../../helpers/constant';

const AddNewAccountForm = ({
    title,
    buttonText,
    isEdit,
    onBack,
    onClose,
    open,
    formData = {},
    setFormData = () => {},
    errorInputNames = [],
    onChangeInput = () => {},
    onSelectCurrency = () => {},
    countries = [],
    saveAccount = () => {},
    isLoading = false,
    setErrorsInputNames = () => {},
    onDeleteAccount,
}) => {
    const safeFormData = {
        beneficiaryName: '',
        beneficiaryAddress: '',
        bankName: '',
        bankAddress: '',
        beneficiaryAccountNumber: '',
        sortCode: '',
        swift: '',
        iban: '',
        additionalBankDetails: '',
        accountNickName: '',
        bankCountry: '',
        beneficiaryCountry: '',
        currency: [],
        ...formData,
    };

    const checkInvalidInput = name => errorInputNames.includes(name);

    const handleCountrySelect = (e, selectedValue, fieldName) => {
        if (errorInputNames.includes(fieldName)) {
            setErrorsInputNames(prev => prev.filter(x => x !== fieldName));
        }
        setFormData(x => ({ ...x, [fieldName]: selectedValue }));
    };

    const handleCurrencySelect = selectedCurrency => {
        if (errorInputNames.includes('currency')) {
            setErrorsInputNames(prev => prev.filter(x => x !== 'currency'));
        }
        onSelectCurrency([selectedCurrency.currencyShortName]);
    };

    let selectedCurrency = fiatCurrencyList.find(
        currency =>
            safeFormData.currency &&
            safeFormData.currency.length > 0 &&
            (currency.currencyShortName === safeFormData.currency[0] ||
                currency.currencyShortName === safeFormData.currency),
    );

    const handleDeleteClick = () => {
        if (onDeleteAccount && formData._id) {
            onDeleteAccount(formData);
        }
    };

    const countryNames = countries.map(country => country.name);

    const getErrorClass = fieldName =>
        checkInvalidInput(fieldName) ? 'border border-red-500' : '';

    return (
        <TransactionCommonModal
            open={open}
            onClose={onClose}
            onBack={onBack}
            heading={title}
            showBackButton={isEdit}
            showCloseIcon={true}>
            <div className="flex flex-col h-[80vh]">
                {/* Scrollable Form Area */}
                <div className="overflow-y-auto pr-2 flex-1 lg:mb-32 md:mb-32 custom-scrollbar">
                    {/* Currency Selection */}
                    <CustomSelect
                        label={'CURRENCY'}
                        options={fiatCurrencyList}
                        value={selectedCurrency}
                        onChange={handleCurrencySelect}
                        isError={checkInvalidInput('currency')}
                        placeholder="Select currency"
                        required={true}
                    />
                    {checkInvalidInput('currency') && (
                        <div className="text-red-500 text-sm mt-2">Currency is required</div>
                    )}

                    <CustomInput
                        label="Account Nickname"
                        name="accountNickName"
                        type="text"
                        value={safeFormData.accountNickName || ''}
                        onChange={onChangeInput}
                        placeholder="Name Account"
                        required={true}
                        className={getErrorClass('accountNickName')}
                    />
                    {checkInvalidInput('accountNickName') && (
                        <div className="text-red-500 text-sm mt-2">
                            Account nickname is required
                        </div>
                    )}

                    <CustomInput
                        label="Beneficiary name"
                        name="beneficiaryName"
                        type="text"
                        value={safeFormData.beneficiaryName || ''}
                        onChange={onChangeInput}
                        placeholder="Enter Beneficiary name"
                        required={true}
                        className={getErrorClass('beneficiaryName')}
                    />
                    {checkInvalidInput('beneficiaryName') && (
                        <div className="text-red-500 text-sm mt-2">
                            Beneficiary name is required
                        </div>
                    )}

                    <CustomInput
                        label="Beneficiary address"
                        name="beneficiaryAddress"
                        type="text"
                        value={safeFormData.beneficiaryAddress || ''}
                        onChange={onChangeInput}
                        placeholder="Enter Beneficiary address"
                    />

                    <SelectWithInput
                        label={'Beneficiary Country'}
                        options={countryNames}
                        selected={safeFormData.beneficiaryCountry}
                        placeholder="Select Beneficiary country"
                        required={true}
                        onChange={(e, value) => handleCountrySelect(e, value, 'beneficiaryCountry')}
                    />
                    {checkInvalidInput('beneficiaryCountry') && (
                        <div className="text-red-500 text-sm mt-2">
                            Beneficiary country is required
                        </div>
                    )}

                    <CustomInput
                        label="Bank name"
                        name="bankName"
                        type="text"
                        value={safeFormData.bankName || ''}
                        onChange={onChangeInput}
                        placeholder="Enter Bank name"
                    />

                    <CustomInput
                        label="Bank Address"
                        name="bankAddress"
                        type="text"
                        value={safeFormData.bankAddress || ''}
                        onChange={onChangeInput}
                        placeholder="Enter Bank Address"
                    />

                    <SelectWithInput
                        label={'Bank Country'}
                        options={countryNames}
                        selected={safeFormData.bankCountry}
                        placeholder="Select Bank country"
                        required={true}
                        onChange={(e, value) => handleCountrySelect(e, value, 'bankCountry')}
                    />
                    {checkInvalidInput('bankCountry') && (
                        <div className="text-red-500 text-sm mt-2">Bank country is required</div>
                    )}

                    <CustomInput
                        label="Account Number"
                        name="beneficiaryAccountNumber"
                        type="text"
                        value={safeFormData.beneficiaryAccountNumber || ''}
                        onChange={onChangeInput}
                        placeholder="Enter Account Number"
                    />

                    <CustomInput
                        label="Sort Code"
                        name="sortCode"
                        type="text"
                        value={safeFormData.sortCode || ''}
                        onChange={onChangeInput}
                        placeholder="Enter Sort Code"
                    />

                    <CustomInput
                        label="Swift"
                        name="swift"
                        type="text"
                        value={safeFormData.swift || ''}
                        onChange={onChangeInput}
                        placeholder="Enter Swift code"
                    />

                    <CustomInput
                        label="IBAN"
                        name="iban"
                        type="text"
                        value={safeFormData.iban || ''}
                        onChange={onChangeInput}
                        placeholder="Enter IBAN"
                    />

                    <CustomInput
                        label="Additional bank info Max. 100"
                        name="additionalBankDetails"
                        type="textarea"
                        value={safeFormData.additionalBankDetails || ''}
                        onChange={onChangeInput}
                        placeholder="EG: routing number (optional)"
                        className="h-32"
                    />

                    <Divider className="my-2" />
                </div>

                {/* Fixed Button Section */}
                <div className="fixed ky bottom-0 left-0 w-full bg-[#FCFCFC] dark:bg-[#26272B] border-t dark:border-[#51525C] border-[#D1D1D6] p-4">
                    <div className="flex flex-row gap-2">
                        {isEdit && (
                            <div
                                className="flex py-2.5 px-3 justify-center items-center gap-2.5 rounded-lg bg-actionBar-button-bg dark:bg-[#3F3F46] cursor-pointer hover:bg-[#E4E4E7] dark:hover:bg-[#4F4F56]"
                                onClick={handleDeleteClick}>
                                <DeleteOutlined className="text-[#18181B] dark:text-white" />
                            </div>
                        )}
                        <button
                            onClick={saveAccount}
                            disabled={isLoading}
                            data-e2e={isLoading ? 'processing-btn' : buttonText}
                            className="w-full p-3 self-stretch rounded-lg font-semibold bg-[#18181B] dark:bg-[#FFF] text-[#FFF] dark:text-[#18181B] flex items-center justify-center uppercase">
                            {isLoading ? (
                                <>
                                    <span className=""> PROCESSING</span>
                                    <CircularProgress className="!h-[19px] !w-[19px] ml-2 dark:text-[#18181B] text-white" />
                                </>
                            ) : (
                                buttonText
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </TransactionCommonModal>
    );
};

export default AddNewAccountForm;
